import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ABP, LocalizationModule, LocalizationService } from '@abp/ng.core';
import { SettingTabsService } from '@abp/ng.setting-management/config';
import { Subscription } from 'rxjs';
import { PaymentSettingsService } from '../proxy/payments/payment-settings.service';
import { MetroToastService } from '../shared/services/metro-toast.service';
import { SharedModule } from '../shared/shared.module';

@Component({
    selector: 'app-custom-settings',
    standalone: true,
    imports: [CommonModule, FormsModule, LocalizationModule, SharedModule],
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss']
})
export class CustomSettingsComponent implements OnInit, OnDestroy {
    private readonly settingTabsService = inject(SettingTabsService);
    private readonly paymentSettingsService = inject(PaymentSettingsService);
    private readonly toastService = inject(MetroToastService);
    private readonly localization = inject(LocalizationService);
    private subscription = new Subscription();

    // Tabs
    settings = signal<ABP.Tab[]>([]);
    selectedTab = signal<ABP.Tab | null>(null);

    visibleSettings = computed(() => {
        return this.settings().filter(tab => !tab.invisible);
    });
    // Payment Gateway
    providerOptions = [
        { value: 'MercadoPago', label: 'Mercado Pago' },
        { value: 'Payway', label: 'Payway' },
        { value: 'Allaria', label: 'Allaria+' }
    ];
    selectedProvider: string = 'MercadoPago';
    minimumAmountForInstallments: number = 100000;
    loading = signal(false);
    saving = signal(false);

    get isPaymentTab(): boolean {
        return this.selectedTab()?.name === 'Pasarela de Pago';
    }

    ngOnInit(): void {
        this.subscription.add(
            this.settingTabsService.visible$.subscribe(tabs => {
                const customTabs: ABP.Tab[] = [
                    ...tabs,
                    {
                        name: 'Pasarela de Pago',
                        component: null as any,
                        order: 30,
                    }
                ];
                this.settings.set(customTabs);
                if (customTabs.length > 0 && !this.selectedTab()) {
                    this.selectedTab.set(customTabs[0]);
                }
            })
        );
        this.loadPaymentSettings();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    selectTab(tab: ABP.Tab): void {
        this.selectedTab.set(tab);
    }

    isTabSelected(tab: ABP.Tab): boolean {
        return this.selectedTab()?.name === tab.name;
    }

    // Payment Gateway methods
    loadPaymentSettings(): void {
        this.loading.set(true);
        this.paymentSettingsService.getActiveProvider().subscribe({
            next: (result: any) => {
                const rawValue = result?.provider ?? result ?? 'MercadoPago';
                if (typeof rawValue === 'string') {
                    // Compatibilidad con configuraciones previas que guardaron varios proveedores separados por coma
                    this.selectedProvider = rawValue.split(',')[0].trim() || 'MercadoPago';
                } else if (Array.isArray(rawValue)) {
                    this.selectedProvider = rawValue[0] || 'MercadoPago';
                } else {
                    this.selectedProvider = 'MercadoPago';
                }
                this.minimumAmountForInstallments = result?.minimumAmountForInstallments ?? 100000;
                this.loading.set(false);
            },
            error: () => {
                this.toastService.error(this.localization.instant('::ErrorLoadingPaymentConfig'), this.localization.instant('::Error'));
                this.loading.set(false);
            }
        });
    }

    savePaymentSettings(): void {
        if (!this.selectedProvider) {
            this.toastService.error(this.localization.instant('::MustSelectProvider'), this.localization.instant('::Warning'));
            return;
        }

        this.saving.set(true);
        this.paymentSettingsService.setActiveProvider({
            provider: this.selectedProvider,
            minimumAmountForInstallments: this.minimumAmountForInstallments
        }).subscribe({
            next: () => {
                this.toastService.success(this.localization.instant('::PaymentGatewaySaved'), this.localization.instant('::Success'));
                this.saving.set(false);
            },
            error: () => {
                this.toastService.error(this.localization.instant('::ErrorSavingPaymentConfig'), this.localization.instant('::Error'));
                this.saving.set(false);
            }
        });
    }
}
